import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { KeyRound, LockKeyhole, LogOut } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/store/auth.store'
import { authService } from '@/modules/auth/services/auth.service'
import { PinInput } from '@/modules/auth/components/PinInput'
import { PasswordInput } from '@/modules/auth/components/PasswordFields'
import { AUTH_INPUT } from '@/modules/auth/components/auth-ui'
import { useLockStore } from '@/modules/auth/lock/idle-lock'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { apiErrorCode } from '@/services/api/errors'
import { ROUTES } from '@/constants/routes'
import { fullName, getInitials } from '@/utils/format'
import { cn } from '@/utils/cn'

/** Time + date above the card, like a device lock screen. */
function LockClock() {
  const [now, setNow] = useState(() => dayjs())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(dayjs()), 15_000)
    return () => window.clearInterval(timer)
  }, [])
  return (
    <div className="mb-6 text-center text-foreground drop-shadow-sm">
      <p className="text-5xl font-semibold tabular-nums tracking-tight sm:text-6xl">{now.format('hh:mm')}</p>
      <p className="mt-1 text-sm font-medium text-foreground/70">{now.format('dddd, D MMMM')}</p>
    </div>
  )
}

/**
 * Full-screen lock over the app (page state stays underneath, hidden and
 * inert). Unlock with the PIN, or the password when no PIN is set / chosen.
 */
export function LockScreen() {
  const { user, logout } = useAuth()
  const clearSession = useAuthStore((s) => s.clearSession)
  const unlockStore = useLockStore((s) => s.unlock)
  const navigate = useNavigate()
  const pinSet = Boolean(user?.security?.pinSet)
  const [mode, setMode] = useState<'pin' | 'password'>(pinSet ? 'pin' : 'password')
  const [pin, setPin] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [shakeKey, setShakeKey] = useState(0)

  // Keep the page underneath out of reach (focus, screen readers, clicks).
  useEffect(() => {
    const root = document.getElementById('root')
    root?.setAttribute('inert', '')
    return () => root?.removeAttribute('inert')
  }, [])

  const submit = async (input: { pin: string } | { password: string }) => {
    setBusy(true)
    setError(null)
    try {
      await authService.unlock(input)
      unlockStore()
    } catch (err) {
      if (apiErrorCode(err) === 'SESSION_ENDED') {
        clearSession()
        toast.error('Too many wrong attempts. Sign in with your password.')
        navigate(ROUTES.LOGIN, { replace: true })
        return
      }
      setError(err instanceof Error ? err.message : 'Could not unlock')
      setPin('')
      setShakeKey((k) => k + 1)
    } finally {
      setBusy(false)
    }
  }

  // Reset happens on the sign-in page (code by email, or admin request).
  const openForgot = async () => {
    if (!user) return
    const forgotEmail = user.email
    await logout()
    navigate(ROUTES.LOGIN, { replace: true, state: { forgotEmail } })
  }

  const signOut = async () => {
    await logout()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  if (!user) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Screen locked"
      className="fixed inset-0 z-[1000] flex animate-fade-in items-center justify-center overflow-y-auto px-4 py-10"
    >
      {/* Frosted backdrop: the app stays visible as colour only — a heavy blur
          plus tint keeps every word unreadable. Solid fallback when the
          browser has no backdrop-filter. */}
      <div
        aria-hidden
        className="fixed inset-0 bg-background/95 supports-[backdrop-filter]:bg-background/45 supports-[backdrop-filter]:backdrop-blur-3xl supports-[backdrop-filter]:backdrop-saturate-150"
      />
      <div
        aria-hidden
        className="fixed inset-0 bg-gradient-to-b from-primary/15 via-background/10 to-black/30 dark:to-black/50"
      />

      <div className="relative w-full max-w-sm">
        <LockClock />
        <div className="overflow-hidden rounded-3xl border border-white/20 bg-card/80 shadow-2xl ring-1 ring-black/[0.06] backdrop-blur-xl dark:border-white/10 dark:bg-card/70 dark:ring-white/[0.06]">
          <div className="auth-brand h-1 w-full" aria-hidden />
          <div className="px-6 pb-6 pt-8 text-center">
            <div className="relative mx-auto mb-4 w-fit">
              <Avatar className="h-16 w-16 ring-4 ring-primary/10">
                {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt="" /> : null}
                <AvatarFallback className="bg-primary/10 text-base font-semibold text-primary">
                  {getInitials(user.firstName, user.lastName)}
                </AvatarFallback>
              </Avatar>
              <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-primary text-primary-foreground">
                <LockKeyhole className="h-3.5 w-3.5" aria-hidden />
              </span>
            </div>
            <h1 className="text-lg font-semibold tracking-tight text-foreground">
              {fullName(user.firstName, user.lastName)}
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {mode === 'pin' ? 'Enter PIN' : 'Enter password'}
            </p>

            <div className="mt-6 min-h-[5.5rem]">
              {mode === 'pin' ? (
                <PinInput
                  value={pin}
                  onChange={(v) => {
                    setPin(v)
                    setError(null)
                  }}
                  onComplete={(v) => void submit({ pin: v })}
                  disabled={busy}
                  error={Boolean(error)}
                  shakeKey={shakeKey}
                />
              ) : (
                <form
                  className={cn('space-y-3 text-left', shakeKey && error ? 'animate-shake' : '')}
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (password) void submit({ password })
                  }}
                >
                  <PasswordInput
                    withIcon
                    autoFocus
                    autoComplete="current-password"
                    placeholder="Password"
                    className={AUTH_INPUT}
                    value={password}
                    disabled={busy}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setError(null)
                    }}
                  />
                  <Button type="submit" loading={busy} disabled={!password} className="h-11 w-full rounded-xl font-semibold">
                    Unlock
                  </Button>
                  <button
                    type="button"
                    className="mx-auto block text-xs font-medium text-primary underline-offset-4 hover:underline disabled:opacity-60"
                    onClick={() => void openForgot()}
                  >
                    Forgot password?
                  </button>
                </form>
              )}
              <p className={cn('mt-3 h-4 text-xs', error ? 'text-destructive' : 'text-transparent')} role="alert">
                {error ?? '·'}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border/50 px-4 py-3">
            {pinSet ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => {
                  setMode(mode === 'pin' ? 'password' : 'pin')
                  setError(null)
                  setPin('')
                  setPassword('')
                }}
              >
                <KeyRound aria-hidden />
                {mode === 'pin' ? 'Use password' : 'Use PIN'}
              </Button>
            ) : (
              <span />
            )}
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => void signOut()}>
              <LogOut aria-hidden />
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
